import { useState } from "react";
import cx from "classnames";
import { ClickableCard } from "@/components/Card";
import { MultiOptionIcon, CheckedIcon } from "@/components/Icon";
import Typography from "@/components/Typography";
import { Trip, useTripsQuery, Division } from "@/fetch/gworld";
import {
  useCurrentTrip,
  useLock,
  useTrackers,
  useDivisionBannerImage,
  useSetTrip,
} from "@/hooks";
import { Skeleton } from "@/components/Loader";
import Button from "@/components/Button";
import MobilePopup from "@/components/MobilePopup";
import { useRouter } from "next/router";
import styles from "./MobileHeader.module.scss";

const MobileTripSelector: React.FC = () => {
  const { currentTrip, setCurrentTrip } = useCurrentTrip();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { data: trips, isLoading: isTripsLoading } = useTripsQuery();
  const { isTripLocked } = useSetTrip();
  const { track } = useTrackers();
  const { getDivisionBanner } = useDivisionBannerImage();

  const filteredTrips =
    trips?.filter((each) => each.division !== Division.FIT) || [];

  const selectTrip = async (trip: Trip) => {
    if (trip.id) setCurrentTrip(trip);
    try {
      // We set query param, so that we can check this query param and
      // bypass the route lock restriction condition in useLock
      await router.replace({
        pathname: "/experience/dashboard",
        query: { tripChanged: true },
      });
      router.replace({
        pathname: "/experience/dashboard",
      });
    } catch (error) {
    } finally {
      track("Trip Switched", {
        eventId: "trip-switched",
        trip_id: trip.trip_id,
        total_trips: trip.count,
        country_code: trip.country_code,
        cid: trip.cid,
        destination: trip.destination,
        division: trip.division,
        duration: trip.duration,
        region: trip.region,
        sales_register_id: trip.sales_register_id,
        sales_register_status: trip.sales_register_status,
        status: trip.status,
        trip_code: trip.trip_code,
        trip_name: trip.trip_name,
      });
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<MultiOptionIcon />}
        className={styles.mobileTripSelector}
        color="info"
        onClick={() => setIsOpen(true)}
      >
        {`${currentTrip?.destination}, ${currentTrip?.division}`}
      </Button>
      {isOpen && (
        <MobilePopup onClose={() => setIsOpen(false)}>
          <div className={styles.tripSelectorPopup}>
            <Typography variant="h6" className={styles.title}>
              Your Trips
            </Typography>
            {filteredTrips?.map((trip) => (
              <div
                key={trip.id}
                role="presentation"
                className={styles.itemContainer}
                onClick={() => {
                  if (isTripLocked(trip)) return;
                  selectTrip(trip);
                }}
              >
                <div className={styles.imageWrapper}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={getDivisionBanner(trip)}
                    alt={trip.trip_name}
                    className={cx(styles.itemImage, {
                      [styles.disableImage]: isTripLocked(trip),
                    })}
                  />
                  {/* {isTripLocked(trip) && (
                    <div className={styles.imageOverlay} />
                  )} */}
                </div>
                <div className={styles.itemDetailsContainer}>
                  <Typography
                    variant="body1"
                    color={
                      isTripLocked(trip) ? "text.secondary" : "text.primary"
                    }
                  >
                    {trip.destination}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {trip.division}
                  </Typography>
                </div>
                {trip.id === currentTrip?.id && (
                  <CheckedIcon
                    className={styles.selectedIcon}
                    color="primary"
                  />
                )}
              </div>
            ))}
          </div>
        </MobilePopup>
      )}
    </>
  );
};

export default MobileTripSelector;
